import type { ApiError, ApiErrorCode } from '@orbit-sync/shared-types';

/** Field path -> message, shaped for rendering next to a form input. */
export type FieldErrors = Record<string, string>;

/**
 * A non-2xx response from the API.
 *
 * Carries the server's `ApiError` body, so callers branch on `code` rather than
 * parsing messages or guessing from the status alone.
 */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly details: ApiError['error']['details'];

  constructor(status: number, body: ApiError) {
    super(body.error.message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = body.error.code;
    this.details = body.error.details;
  }

  /** Validation problems keyed by field, for inline display. Empty when there are none. */
  fieldErrors(): FieldErrors {
    const errors: FieldErrors = {};

    for (const detail of this.details ?? []) {
      // First message per field wins; a second one on the same input is noise.
      errors[detail.path] ??= detail.message;
    }

    return errors;
  }
}

/** The network itself failed - no response, so no `ApiError` body exists. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Could not reach the server. Check your connection and try again.');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export function isApiRequestError(error: unknown): error is ApiRequestError {
  return error instanceof ApiRequestError;
}

/** A message safe to show a user, whatever went wrong. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError || error instanceof NetworkError) {
    return error.message;
  }

  return 'Something went wrong. Please try again.';
}

/** Field errors if the failure was a validation error, otherwise empty. */
export function fieldErrorsFrom(error: unknown): FieldErrors {
  return isApiRequestError(error) ? error.fieldErrors() : {};
}
